import { type GetApiAuthorsQueryParams } from '@/shared/api'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import { authorCreate, authorDelete, authorUpdate } from '../api'
import {
  type AuthorsQueryModelWithPagination,
  type AuthorsSortState,
  createAuthorsQueryModel,
} from '../lib/authors-query-model'

interface AuthorsTableGateProps {
  initialFilters?: GetApiAuthorsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: AuthorsSortState
}
export const AuthorsTableGate = createGate<AuthorsTableGateProps>()

export const AuthorsTableAuthorsQueryModel = createAuthorsQueryModel({
  usePagination: true,
  accumulate: false,
}) as AuthorsQueryModelWithPagination
const { availableSet, unavailableSet } = AuthorsTableAuthorsQueryModel
export const {
  //   Query
  query: authorsTableQuery,
  //   Init
  $filters: $authorsTableFilters,
  filtersInit: authorsTableFiltersInit,
  //   Search
  $search: $authorsTableSearch,
  searchChanged: authorsTableSearchChanged,
  searchReset: authorsTableSearchReset,
  //   Sort
  $sort: $authorsTableSort,
  sortChanged: authorsTableSortChanged,
  sortReset: authorsTableSortReset,
  //   Pagination
  $page: $authorsTablePage,
  pageChanged: authorsTablePageChanged,
  pageReset: authorsTablePageReset,
  $pageSize: $authorsTablePageSize,
  pageSizeChanged: authorsTablePageSizeChanged,
  //   Refresh
  refreshed: authorsTableRefreshed,
} = AuthorsTableAuthorsQueryModel

sample({
  clock: AuthorsTableGate.open,
  target: availableSet,
})

// Search input

export const $authorsTableSearchInputValue = createStore('')
  .on(authorsTableSearchChanged, (_, q) => q)
  .reset(authorsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: AuthorsTableGate.open,
  source: combine(
    AuthorsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: authorsTablePageChanged,
})
// Page Size
sample({
  clock: AuthorsTableGate.open,
  source: combine(
    AuthorsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: authorsTablePageSizeChanged,
})
// Search
sample({
  clock: AuthorsTableGate.open,
  source: combine(
    AuthorsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: authorsTableSearchChanged,
})
// Sort
sample({
  clock: AuthorsTableGate.open,
  source: combine(
    AuthorsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: authorsTableSortChanged,
})
// Filters
sample({
  source: AuthorsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: authorsTableFiltersInit,
})

sample({
  clock: AuthorsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $authorsTablePage,
    queryData: authorsTableQuery.$data,
  }),
  clock: authorDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: authorsTablePageChanged,
})

sample({
  clock: [
    authorCreate.finished.success,
    authorDelete.finished.success,
    authorUpdate.finished.success,
  ],
  target: authorsTableRefreshed,
})
